// src/pages/store/listings/CreateListingForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Loading from '../../../components/common/Loading';
import ConfirmationModal from '../../../components/common/ConfirmationModal';

// Import view components
import MobileCreateListing from './MobileCreateListing';
import DesktopCreateListing from './DesktopCreateListing';

// Import hooks and utilities
import { useListingForm } from '../../../hooks/useListingForm';
import { useCategoryData } from '../../../hooks/useCategoryData';
import { useLocationData } from '../../../hooks/useLocationData';
import { validateForm } from '../../../utils/listingValidation';
import { createListingHandler } from '../../../services/listingService';

export default function CreateListingForm({ onSuccess, onCancel }) {
  const navigate = useNavigate();
  const { form, updateForm, resetForm } = useListingForm();
  const { user } = useAuth();
  
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  
  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null
  });

  // Custom hooks
  const { 
    allCategories, 
    mainCategories, 
    subcategories, 
    categoryType,
    loadCategories,
    updateSubcategories,
    updateCategoryType
  } = useCategoryData();

  const {
    states,
    lgas,
    lcdas,
    showLcdaField,
    loadingStates,
    loadingLgas,
    loadingLcdas,
    loadLocationData,
    fetchLgas,
    fetchLcdas
  } = useLocationData();

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await loadCategories();
        await loadLocationData(user, updateForm);
      } catch (err) {
        // console.error('❌ Error fetching initial data:', err);
        setErrors({ general: 'Failed to load form data. Please try again.' });
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [user, loadCategories, loadLocationData, updateForm]);

  const handleFieldChange = useCallback((field, value) => {
    // console.log(`🔄 Field change: ${field} = ${value}`);
    updateForm({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors, updateForm]);

  const handleImagesChange = useCallback((newImages) => {
    setImages(newImages);
    if (errors.images) {
      setErrors(prev => ({ ...prev, images: '' }));
    }
  }, [errors.images]);

  const handleImageUploadProgress = useCallback((progress) => {
    setImageUploadProgress(progress);
  }, []);

  const showConfirmation = (title, message, onConfirm) => {
    setConfirmConfig({ title, message, onConfirm });
    setShowConfirmModal(true);
  };

  const closeModal = () => {
    setShowConfirmModal(false);
  };

  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  const shouldShowCondition = useCallback(() => {
    if (!form.category_id) return true;
    
    const selectedCategory = allCategories.find(cat => cat.id === parseInt(form.category_id));
    if (!selectedCategory) return true;
    
    const excludedCategoryTypes = ['services', 'properties'];
    const excludedMainCategories = ['Food', 'Food, Agriculture & Farming', 'Agriculture', 'Farming'];
    
    if (excludedCategoryTypes.includes(categoryType)) {
      return false;
    }
    
    if (excludedMainCategories.includes(selectedCategory.name)) {
      return false;
    }
    
    if (selectedCategory.parent_id) {
      const parentCategory = allCategories.find(cat => cat.id === selectedCategory.parent_id);
      if (parentCategory && excludedMainCategories.includes(parentCategory.name)) {
        return false;
      }
    }
    
    return true;
  }, [categoryType, form.category_id, allCategories]);

  const handleSubmit = async () => {
    // console.log('🔄 Submit button pressed');
    // console.log('🔍 Current form state keys:', Object.keys(form));
    
    const formErrors = validateForm(form, images, showLcdaField, categoryType);
    
    if (Object.keys(formErrors).length > 0) {
      // console.log('❌ Validation errors:', formErrors);
      setErrors(formErrors);
      return;
    }

    showConfirmation(
      'Create Listing',
      'Are you sure you want to create this listing?',
      () => handleCreateListing()
    );
  };

  const handleCreateListing = async () => {
    await createListingHandler({
      form,
      images,
      user,
      showLcdaField,
      categoryType,
      setSubmitting,
      setErrors,
      setImageUploadProgress,
      onSuccess: (listingId) => {
        if (onSuccess) {
          onSuccess(listingId);
        } else {
          navigate(`/store/listings/listing-details/${listingId}`);
        }
      },
      resetForm,
      setImages
    });
  };

  // Category selection handlers
  const handleMainCategorySelect = useCallback((categoryId) => {
    // console.log('🎯 Main category selected:', categoryId);
    updateForm({ 
      category_id: categoryId,
      subcategory_id: '' 
    });
    updateSubcategories(categoryId, allCategories);
  }, [allCategories, updateForm, updateSubcategories]);

  const handleSubcategorySelect = useCallback((subcategoryId) => {
    // console.log('🎯 Subcategory selected:', subcategoryId);
    updateForm({ subcategory_id: subcategoryId });
    updateCategoryType(subcategoryId, allCategories);
  }, [allCategories, updateForm, updateCategoryType]);

  // Location selection handlers
  const handleStateSelect = useCallback(async (stateId) => {
    // console.log('📍 State selected:', stateId);
    updateForm({ 
      state_id: stateId,
      lga_id: '',
      lcda_id: ''
    });
    await fetchLgas(stateId);
  }, [updateForm, fetchLgas]);

  const handleLgaSelect = useCallback(async (lgaId) => {
    // console.log('📍 LGA selected:', lgaId);
    updateForm({ 
      lga_id: lgaId,
      lcda_id: ''
    });
    
    await fetchLcdas(lgaId);
  }, [updateForm, fetchLcdas]);

  const handleLcdaSelect = useCallback((lcdaId) => {
    // console.log('📍 LCDA selected:', lcdaId);
    updateForm({ lcda_id: lcdaId });
  }, [updateForm]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading size="large" />
          <p className="mt-4 text-gray-600">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile View */}
      <MobileCreateListing
        form={form}
        errors={errors}
        submitting={submitting}
        images={images}
        imageUploadProgress={imageUploadProgress}
        allCategories={allCategories}
        mainCategories={mainCategories}
        subcategories={subcategories}
        categoryType={categoryType}
        states={states}
        lgas={lgas}
        lcdas={lcdas}
        showLcdaField={showLcdaField}
        loadingStates={loadingStates}
        loadingLgas={loadingLgas}
        loadingLcdas={loadingLcdas}
        onBack={handleBack}
        onSubmit={handleSubmit}
        onFieldChange={handleFieldChange}
        onImagesChange={handleImagesChange}
        onMainCategorySelect={handleMainCategorySelect}
        onSubcategorySelect={handleSubcategorySelect}
        onStateSelect={handleStateSelect}
        onLgaSelect={handleLgaSelect}
        onLcdaSelect={handleLcdaSelect}
        onImageUploadProgress={handleImageUploadProgress}
        shouldShowCondition={shouldShowCondition}
      />

      {/* Desktop View - REMOVED specializedData props since we use form directly */}
      <DesktopCreateListing
        form={form}
        errors={errors}
        submitting={submitting}
        images={images}
        imageUploadProgress={imageUploadProgress}
        allCategories={allCategories}
        mainCategories={mainCategories}
        subcategories={subcategories}
        categoryType={categoryType}
        states={states}
        lgas={lgas}
        lcdas={lcdas}
        showLcdaField={showLcdaField}
        loadingStates={loadingStates}
        loadingLgas={loadingLgas}
        loadingLcdas={loadingLcdas}
        onBack={handleBack}
        onSubmit={handleSubmit}
        onFieldChange={handleFieldChange}
        onImagesChange={handleImagesChange}
        onMainCategorySelect={handleMainCategorySelect}
        onSubcategorySelect={handleSubcategorySelect}
        onStateSelect={handleStateSelect}
        onLgaSelect={handleLgaSelect}
        onLcdaSelect={handleLcdaSelect}
        onImageUploadProgress={handleImageUploadProgress}
        shouldShowCondition={shouldShowCondition}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={closeModal}
        onConfirm={() => {
          confirmConfig.onConfirm?.();
          closeModal();
        }}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Create Listing"
        cancelText="Cancel"
      />
    </>
  );
}